import { StatusMessage } from "@brexhq/component-library";
import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { margin } from "polished";
import React from "react";

import { BodyText } from "@brexhq/next/src/components/deprecated/Typography";
import {
  desktop,
  tablet,
  mobile,
  type SpacingValues,
} from "@brexhq/shared/types/SpacingScale";

import { genericErrorMessage, MarketoFormProps, SubmitError } from "../types";

type SuccessMessageProps = Pick<
  MarketoFormProps,
  | "successMessage"
  | "anchor"
  | "theme"
  | "hideSuccessMessageImage"
  | "topSpacing"
  | "showSuccessMessageBackgroundColor"
>;

const StatusWrapper = styled.div<{
  topSpacing?: SpacingValues;
  showSuccessMessageBackgroundColor?: boolean | null;
}>(({ theme, topSpacing, showSuccessMessageBackgroundColor }) => {
  const backgroundColor = showSuccessMessageBackgroundColor
    ? theme.colors.background.primary
    : "transparent";
  const padding = showSuccessMessageBackgroundColor ? theme.spacing[16] : 0;
  const borderRadius = showSuccessMessageBackgroundColor
    ? theme.borders.radius.small
    : 0;

  if (!topSpacing) {
    return {
      ...margin("auto"),
      display: "flex",
      justifyContent: "center",
      backgroundColor,
      padding,
      borderRadius,
    };
  }

  const {
    breakpoints: { MED, SML },
  } = theme;

  return {
    backgroundColor,
    ...margin(desktop[topSpacing], "auto"),
    padding,
    borderRadius,
    display: "flex",
    justifyContent: "center",
    [MED]: { ...margin(tablet[topSpacing], "auto") },
    [SML]: { ...margin(mobile[topSpacing], "auto") },
  };
});

export const SuccessMessage: React.FC<SuccessMessageProps> = ({
  successMessage,
  anchor,
  theme,
  hideSuccessMessageImage,
  topSpacing,
  showSuccessMessageBackgroundColor,
}) => {
  const { colors } = useTheme();
  const anchorProps = anchor ? { id: anchor } : {};

  let description = "Your info has been successfully submitted.";
  let title = "Submitted!";

  if (typeof successMessage === "string") {
    description = successMessage;
  }
  if (typeof successMessage === "object") {
    title = successMessage.title;
    description = successMessage.description;
  }

  return (
    <StatusWrapper
      topSpacing={topSpacing}
      showSuccessMessageBackgroundColor={showSuccessMessageBackgroundColor}
      {...anchorProps}
    >
      <StatusMessage
        graphic={
          !hideSuccessMessageImage
            ? "https://brand.brex.com/transform/ba98472b-efde-4119-a260-e8aebf1a00a0/Congrats-Light"
            : undefined
        }
        title={
          <BodyText
            textModifier={"XL_MED"}
            color={
              (theme === "PrimaryDark" ||
                theme === "Dark" ||
                theme === "PrimaryGray") &&
              !showSuccessMessageBackgroundColor
                ? colors.content.inverse.primary
                : colors.content.primary
            }
          >
            {title}
          </BodyText>
        }
        description={
          <BodyText
            textModifier="MED_LIGHT"
            color={
              (theme === "PrimaryDark" ||
                theme === "Dark" ||
                theme === "PrimaryGray") &&
              !showSuccessMessageBackgroundColor
                ? colors.content.inverse.primary
                : colors.content.primary
            }
          >
            {description}
          </BodyText>
        }
      />
    </StatusWrapper>
  );
};

interface ErrorMessageProps extends Pick<MarketoFormProps, "anchor"> {
  submitError: SubmitError;
}

export const ErrorMessage: React.FC<ErrorMessageProps> = ({
  anchor,
  submitError,
}) => {
  const { colors } = useTheme();
  const anchorProps = anchor ? { id: anchor } : {};

  return (
    <StatusWrapper className="chromatic-ignore" {...anchorProps}>
      <StatusMessage
        graphic="https://brand.brex.com/transform/b0634d9b-adce-46ce-8033-45ba01daec8a/backend-crash"
        title={
          <BodyText textModifier={"XL_MED"} color={colors.content.primary}>
            {submitError?.title || genericErrorMessage.title}
          </BodyText>
        }
        description={submitError?.message || genericErrorMessage.message}
      />
    </StatusWrapper>
  );
};
